import { Box, Typography, Skeleton } from '@mui/material';
import { styled } from '@mui/material/styles';
import useCustomTheme from '../../../theme/useCustomTheme';
import '@fontsource/rubik';

const CardWrap = styled(Box)(({ theme }) => ({
  backgroundColor: '#ffffff',
  border: '1px solid #E9EBE9',
  borderRadius: '14px',
  padding: '18px 20px',
  boxShadow: '0 1px 2px rgba(8,35,27,0.04)',
  flex: 1,
  minWidth: 0,
}));

const EyebrowRow = styled(Box)({
  display: 'flex',
  justifyContent: 'space-between',
  marginBottom: 12,
});

const Eyebrow = styled(Typography)({
  fontFamily: 'Rubik',
  fontSize: 11,
  fontWeight: 700,
  color: '#6b7470',
  letterSpacing: '0.1em',
  textTransform: 'uppercase',
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
});

function UsageTile({ label, used, remaining, total, barColor, isLoading }) {
  const hasContract = typeof total === 'number' && total > 0;
  const pct = hasContract ? Math.round((used / total) * 100) : 0;
  const remPct = hasContract ? 100 - pct : 0;

  return (
    <CardWrap>
      <EyebrowRow>
        <Eyebrow>{label}</Eyebrow>
        <Eyebrow>In contract</Eyebrow>
      </EyebrowRow>

      {isLoading ? (
        <>
          <Skeleton width="80%" height={32} sx={{ mb: 1 }} />
          <Skeleton width="100%" height={8} sx={{ borderRadius: 99 }} />
        </>
      ) : (
        <>
          {/* Numbers row */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'baseline',
              justifyContent: 'space-between',
              mb: 1.5,
              flexWrap: 'nowrap',
              gap: 1,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1.5 }}>
              <Box>
                <Typography
                  component="span"
                  sx={{
                    fontFamily: 'Rubik',
                    fontSize: { xs: '22px', lg: '26px', xl: '32px' },
                    fontWeight: 700,
                    letterSpacing: '-0.02em',
                    color: '#08231B',
                  }}
                >
                  {used}
                </Typography>
                <Typography
                  component="span"
                  sx={{
                    fontFamily: 'Rubik',
                    fontSize: { xs: '12px', lg: '13px' },
                    color: '#08231B',
                    ml: 0.5,
                  }}
                >
                  used
                </Typography>
              </Box>
              <Box>
                <Typography
                  component="span"
                  sx={{
                    fontFamily: 'Rubik',
                    fontSize: { xs: '22px', lg: '26px', xl: '32px' },
                    fontWeight: 700,
                    letterSpacing: '-0.02em',
                    color: '#08231B',
                  }}
                >
                  {remaining}
                </Typography>
                <Typography
                  component="span"
                  sx={{
                    fontFamily: 'Rubik',
                    fontSize: { xs: '12px', lg: '13px' },
                    color: '#6b7470',
                    ml: 0.5,
                  }}
                >
                  remaining
                </Typography>
              </Box>
            </Box>
            <Typography
              sx={{
                fontFamily: 'Rubik',
                fontSize: { xs: '22px', lg: '24px', xl: '28px' },
                fontWeight: 700,
                letterSpacing: '-0.02em',
                color: '#08231B',
              }}
            >
              {total}
            </Typography>
          </Box>

          {/* Segmented bar */}
          <Box
            sx={{
              height: 8,
              borderRadius: '99px',
              backgroundColor: '#ececea',
              overflow: 'hidden',
              mb: 1,
              display: 'flex',
            }}
          >
            <Box
              sx={{
                width: `${pct}%`,
                height: '100%',
                backgroundColor: barColor,
                transition: 'width 0.4s ease',
              }}
            />
          </Box>

          {/* Legend */}
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              fontSize: { xs: '10px', lg: '11px', xl: '12px' },
              fontFamily: 'Rubik',
              color: '#6b7470',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
              <Box
                sx={{
                  width: 9,
                  height: 9,
                  borderRadius: '2px',
                  backgroundColor: barColor,
                  flexShrink: 0,
                }}
              />
              <Typography sx={{ fontFamily: 'Rubik', fontSize: 'inherit', color: 'inherit' }}>
                {used} used{hasContract ? ` · ${pct}%` : ''}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8, minWidth: 0 }}>
              <Box
                sx={{
                  width: 9,
                  height: 9,
                  borderRadius: '2px',
                  border: '1px solid #E9EBE9',
                  backgroundColor: '#f6f6f4',
                  flexShrink: 0,
                }}
              />
              <Typography sx={{ fontFamily: 'Rubik', fontSize: 'inherit', color: 'inherit', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {remaining} remaining{hasContract ? ` · ${remPct}%` : ''}
              </Typography>
            </Box>
          </Box>
        </>
      )}
    </CardWrap>
  );
}

export default function UsageMetricsRow({ summary, isLoading }) {
  const [theme] = useCustomTheme();

  const creditBased      = summary?.credit_based ?? false;
  const videosUsed       = summary?.videos_used ?? 0;
  const videosTotal      = summary?.videos_total;
  const videosRemaining  = summary?.videos_remaining;
  const qtUsed           = summary?.quick_turns_used ?? 0;
  const qtTotal          = summary?.quick_turns_total;
  const qtRemaining      = summary?.quick_turns_remaining;
  const creditsUsed      = summary?.credits_used ?? 0;
  const creditsTotal     = summary?.credits_total;
  const creditsRemaining = summary?.credits_remaining;

  return (
    <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', sm: 'row' } }}>
      {creditBased ? (
        <UsageTile
          label="Video Credits"
          used={creditsUsed}
          remaining={creditsRemaining != null ? creditsRemaining : 'NA'}
          total={creditsTotal != null ? creditsTotal : 'NA'}
          barColor={theme.palette.primary.main}
          isLoading={isLoading}
        />
      ) : (
        <UsageTile
          label="Videos"
          used={videosUsed}
          remaining={videosRemaining != null ? videosRemaining : 'NA'}
          total={videosTotal != null ? videosTotal : 'NA'}
          barColor={theme.palette.primary.main}
          isLoading={isLoading}
        />
      )}
      <UsageTile
        label="24-hr Quick Turns"
        used={qtUsed}
        remaining={qtRemaining != null ? qtRemaining : 'NA'}
        total={qtTotal != null ? qtTotal : 'NA'}
        barColor="#08231B"
        isLoading={isLoading}
      />
    </Box>
  );
}
